import { Component, Host, h, Element, State, Prop, Listen } from '@stencil/core';
import {
  attachSlottedCss,
  BreakpointCustomizable,
  checkSlotContent,
  createUuid,
  injectCSS,
  injectFont,
  observeOptions,
  TValidationState,
} from '../../utils';
import { createStyles, getSlottedCss } from './dropdown-wrapper-styles';
import {
  closeOtherDropdowns,
  addDocumentEventListeners,
  removeDocumentEventListeners,
  TDropdownLabelPosition,
} from './dropdown-wrapper-utils';
import { StateMessage } from '../common/state-message/state-message';

@Component({
  tag: 'dc-ui-dropdown-wrapper',
  shadow: true,
})
export class DcUiDropdownWrapper {
  @Element() el: HTMLElement;
  @Prop() label = '';
  @Prop() labelPosition: TDropdownLabelPosition = 'default';
  @Prop() labelWidth?: BreakpointCustomizable<string> = '25%';
  @Prop() disabled = false;
  @Prop() validationstate: TValidationState = 'none';
  @Prop() message = '';
  @State() open = false;
  @State() selectedOptionIndex: number = null;
  @State() currentSearchIndex: number = 0;

  nativeSelectEl: HTMLSelectElement;
  dropdownEl: HTMLElement;
  headEl: HTMLElement;
  headTextEl: HTMLElement;
  listEl: HTMLElement;
  nativeSelectWrapperEl: HTMLElement;
  selectObserver: MutationObserver;
  useNativeSelect = false;
  uuid: string;
  private searchBuffer: string = '';
  private searchTimer: NodeJS.Timeout | null = null;

  @Listen('keydown')
  handleDocumentKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab' && this.open) {
      this.open = false;
    }
  }

  componentWillLoad() {
    injectFont();
    checkSlotContent(this.el, 'select');
    this.uuid = createUuid();

    this.nativeSelectEl = this.el.querySelector('select');
    if (!this.nativeSelectEl) {
      console.warn('dropdown has no select element');
      return;
    }

    // if on a mobile (touch) device, use the native element instead of the custom dropdown
    this.useNativeSelect = 'ontouchstart' in window;
    if (this.useNativeSelect) {
      return;
    }

    // if using the custom dropdown, synchronize it with the native element (in case of external changes)
    this.nativeSelectEl.setAttribute('aria-hidden', 'true');
    this.nativeSelectEl.addEventListener('change', () => {
      this.updateSelectedOptionFromNativeSelect();
    });

    this.observeNativeSelectOptions();
  }

  componentDidLoad() {
    if (!this.nativeSelectEl) {
      return;
    }

    if (this.useNativeSelect) {
      attachSlottedCss(
        this.el,
        getSlottedCss(this.el, this.useNativeSelect, this.labelPosition, this.validationstate)
      );
      if (this.disabled) {
        this.nativeSelectEl.setAttribute('disabled', 'true');
      }
      return;
    }

    // if using the custom dropdown, load the options from the native element
    // and watch for external changes to them
    this.loadOptions();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.selectObserver = new MutationObserver((_mutations, _observer) => {
      this.loadOptions();
    });
    this.selectObserver.observe(this.nativeSelectEl, {
      attributes: false,
      childList: true,
      subtree: true,
    });

    addDocumentEventListeners(this);
  }

  connectedCallback(): void {
    this.observeNativeSelectOptions();
  }

  disconnectedCallback() {
    if (this.useNativeSelect) {
      return;
    }
    removeDocumentEventListeners(this);
  }

  loadOptions = (): void => {
    if (!this.nativeSelectEl) {
      return;
    }

    this.listEl.innerHTML = '';

    const optGroups = this.nativeSelectEl.querySelectorAll('optgroup');
    if (optGroups.length) {
      this.listEl.classList.add('has-optgroups');
      Array.from(optGroups).forEach((optGroup) => {
        this.addOptGroup(optGroup);
      });
    } else {
      const ul = document.createElement('ul');
      ul.setAttribute('role', 'listbox');
      ul.setAttribute('aria-labelledby', `${this.uuid}_label`);
      this.listEl.appendChild(ul);
      Array.from(this.nativeSelectEl.options).forEach((nativeOptionEl) => {
        this.addOption(nativeOptionEl, ul);
      });
    }

    this.updateSelectedOptionFromNativeSelect();
  };

  addOptGroup = (optGroup: HTMLOptGroupElement): void => {
    const optGroupEl = document.createElement('ul');
    optGroupEl.setAttribute('role', 'listbox');
    optGroupEl.setAttribute('aria-labelledby', `${this.uuid}_label`);
    this.listEl.appendChild(optGroupEl);
    optGroupEl.setAttribute('role', 'group');

    const optGroupHeadingEl = document.createElement('li');
    optGroupEl.appendChild(optGroupHeadingEl);
    optGroupHeadingEl.setAttribute('role', 'presentation');
    optGroupHeadingEl.classList.add('dropdown-optgroup-heading');
    optGroupHeadingEl.innerHTML = optGroup.label;

    const index = this.listEl.children.length - 1;
    optGroupHeadingEl.id = `${this.uuid}_optgroup-${index}`;
    optGroupEl.setAttribute('aria-labelledby', optGroupHeadingEl.id);

    Array.from(optGroup.children).forEach((nativeOptionEl) => {
      this.addOption(nativeOptionEl as HTMLOptionElement, optGroupEl);
    });
  };

  addOption = (nativeOptionEl: HTMLOptionElement, parent: HTMLUListElement): void => {
    const index = this.listEl.querySelectorAll('.dropdown-option').length;
    nativeOptionEl.setAttribute(`data-${this.uuid}-index`, `${index}`);

    const optionEl = document.createElement('li');
    optionEl.classList.add('dropdown-option');
    optionEl.innerHTML = nativeOptionEl.innerHTML;
    optionEl.id = `${this.uuid}_option-${index}`;
    optionEl.setAttribute(`data-${this.uuid}-index`, `${index}`);
    optionEl.setAttribute('role', 'option');
    optionEl.setAttribute('aria-selected', 'false');

    const icon = document.createElement('dc-ui-icon');
    icon.classList.add('checkmark-icon');
    icon.setAttribute('name', 'dc_checkmark');
    icon.setAttribute('size', 'tiny');
    icon.setAttribute('color', 'darkBlue02');
    optionEl.appendChild(icon);

    optionEl.addEventListener('click', () => {
      this.selectOption(optionEl);
    });

    parent.appendChild(optionEl);
  };

  private observeNativeSelectOptions = (): void => {
    observeOptions(this.nativeSelectEl, () => {
      this.loadOptions();
    });
  };

  private updateSelectedOptionFromNativeSelect = (): void => {
    const selectedNativeOptionEl = this.nativeSelectEl.querySelector('option:checked');
    if (selectedNativeOptionEl) {
      const index = +selectedNativeOptionEl.getAttribute(`data-${this.uuid}-index`);
      const selectedOptionEl = this.optionElByIndex(index);
      this.selectOption(selectedOptionEl, false);
    }
  };

  private selectOption = (optionEl: HTMLElement, fireEvent = true): void => {
    Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((otherOptionEl) => {
      otherOptionEl.setAttribute('aria-selected', 'false');
    });
    optionEl.setAttribute('aria-selected', 'true');

    this.headTextEl.innerHTML = optionEl.textContent;
    this.selectedOptionIndex = +optionEl.getAttribute(`data-${this.uuid}-index`);
    this.nativeSelectEl.value = this.nativeOptionElByIndex(this.selectedOptionIndex).value;

    if (fireEvent) {
      this.nativeSelectEl.dispatchEvent(new Event('change', { bubbles: true })); // bubbles: true is required for the event to be caught in react
    }
  };

  private onHeadKeydown = (e: KeyboardEvent): void => {
    // Handle space/enter key
    if (['SpaceBar', 'Enter', ' '].includes(e.key)) {
      e.preventDefault(); // Prevent page scroll on space

      if (this.open) {
        // If dropdown is open, handle selection
        const activeOption = this.listEl.querySelector('.dropdown-option.active') as HTMLElement;
        if (activeOption) {
          this.selectOption(activeOption);
          this.open = false; // Close after selection
        }
      } else {
        // If dropdown is closed, open it
        this.open = true;
        if (this.selectedOptionIndex !== null) {
          // Highlight the currently selected option when opening
          this.highlightSelectedOption();
        }
      }
      return;
    }

    // Only handle other keyboard interactions if dropdown is open
    if (!this.open) return;

    // Handle arrow key navigation (only highlight, don't select)
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const offset = ['ArrowDown', 'ArrowRight'].includes(e.key) ? 1 : -1;
      const currentActive = this.listEl.querySelector('.dropdown-option.active') as HTMLElement;
      const currentIndex = currentActive
        ? +currentActive.getAttribute(`data-${this.uuid}-index`)
        : this.selectedOptionIndex;
      const nextIndex = currentIndex + offset;
      const nextOptionEl = this.optionElByIndex(nextIndex);

      if (nextOptionEl) {
        // Remove active class from all options
        Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((optionEl) => {
          optionEl.classList.remove('active');
        });
        // Add active class to next option
        nextOptionEl.classList.add('active');
      }
      return;
    }

    // Handle backspace to allow search correction
    if (e.key === 'Backspace') {
      e.preventDefault();
      this.searchBuffer = this.searchBuffer.slice(0, -1);
      this.currentSearchIndex = 0;
      if (this.searchBuffer) {
        this.performSearch();
      }
      return;
    }

    // Handle character input
    if (e.key.length === 1) {
      e.preventDefault();

      // Clear previous timer if it exists
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      const lastChar = e.key.toLowerCase();
      // If typing the same character as last time, cycle through matches
      if (this.searchBuffer && lastChar === this.searchBuffer[this.searchBuffer.length - 1]) {
        this.currentSearchIndex =
          (this.currentSearchIndex + 1) % this.listEl.querySelectorAll('.dropdown-option').length;
        this.performSearch();
      } else {
        // New character sequence, append to buffer
        this.searchBuffer += lastChar;
        this.currentSearchIndex = 0;
        this.performSearch();
      }

      // Clear the search buffer after 500ms of no input
      this.searchTimer = setTimeout(() => {
        this.searchBuffer = '';
        this.currentSearchIndex = 0;
      }, 500);
    }
  };

  private performSearch = (): void => {
    const options = Array.from(this.listEl.querySelectorAll('.dropdown-option'));

    // Find all options that match the search buffer
    const matchingOptions = options.filter((option) =>
      option.textContent.trim().toLowerCase().startsWith(this.searchBuffer)
    );

    if (matchingOptions.length > 0) {
      // Get the current matching option
      const matchingOption = matchingOptions[this.currentSearchIndex % matchingOptions.length];

      // Remove active class from all options
      options.forEach((optionEl) => {
        optionEl.classList.remove('active');
      });

      // Only highlight the matching option visually
      matchingOption.classList.add('active');
    }
  };

  private highlightSelectedOption = (): void => {
    if (this.selectedOptionIndex !== null) {
      const selectedOption = this.optionElByIndex(this.selectedOptionIndex);
      if (selectedOption) {
        // Remove active class from all options first
        Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((optionEl) => {
          optionEl.classList.remove('active');
        });
        // Add active class to selected option
        selectedOption.classList.add('active');
      }
    }
  };

  private onHeadClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (this.disabled) {
      return;
    }
    closeOtherDropdowns(this);
    if (!this.useNativeSelect) {
      this.open = !this.open;
      // Highlight and scroll to selected option when opening dropdown
      if (this.open) {
        this.highlightSelectedOption();
      }
    }
  };

  private optionElByIndex = (index: number): HTMLElement => {
    return this.listEl.querySelector(`[data-${this.uuid}-index="${index}"]`);
  };

  private nativeOptionElByIndex = (index: number): HTMLOptionElement => {
    return this.nativeSelectEl.querySelector(`option[data-${this.uuid}-index="${index}"]`);
  };

  render() {
    injectCSS(
      this.el,
      createStyles,
      this.labelPosition,
      this.disabled,
      this.useNativeSelect,
      this.validationstate,
      this.labelWidth,
      this.open
    );

    const arrowIconColor = this.disabled ? 'grey03' : 'darkBlue02';

    const label = (
      <label
        id={`${this.uuid}_label`}
        aria-hidden={this.labelPosition === 'default' ? 'true' : 'false'}>
        {this.label}
      </label>
    );

    return (
      <Host data-uuid={this.uuid}>
        {!this.useNativeSelect && <slot></slot>}

        <div class="container">
          {this.labelPosition !== 'default' && label}

          {this.useNativeSelect ? (
            <div
              ref={(el) => (this.nativeSelectWrapperEl = el)}
              class={{
                'native-select-wrapper': true,
                'dropdown-head': true,
                disabled: this.disabled,
                error: this.validationstate === 'error',
              }}>
              {this.labelPosition === 'default' && label}
              <slot></slot>
              <dc-ui-icon
                class="arrow-icon"
                name="dc_down"
                size="tiny"
                color={arrowIconColor}></dc-ui-icon>
            </div>
          ) : (
            <div
              class="dropdown"
              ref={(el) => (this.dropdownEl = el)}>
              <button
                ref={(el) => (this.headEl = el)}
                class={{
                  'dropdown-head': true,
                  disabled: this.disabled,
                  error: this.validationstate === 'error',
                }}
                tabindex={this.disabled ? '-1' : '0'}
                role="combobox"
                aria-controls={`${this.uuid}_list`}
                aria-expanded={this.open ? 'true' : 'false'}
                aria-haspopup="listbox"
                aria-labelledby={`${this.uuid}_label`}
                aria-activedescendant={`${this.uuid}_option-${this.selectedOptionIndex}`}
                aria-live={this.open ? 'off' : 'polite'}
                onClick={this.onHeadClick}
                onKeyDown={this.onHeadKeydown}>
                {this.labelPosition === 'default' && label}
                <span
                  ref={(el) => (this.headTextEl = el)}
                  class="dropdown-head-text"
                  tabindex="-1"></span>
                <dc-ui-icon
                  class="arrow-icon"
                  name="dc_down"
                  size="tiny"
                  color={arrowIconColor}></dc-ui-icon>
              </button>

              <div
                id={`${this.uuid}_list`}
                ref={(el) => (this.listEl = el)}
                tabindex="-1"
                class={{
                  'dropdown-list': true,
                  open: this.open,
                }}></div>
            </div>
          )}

          {this.validationstate !== 'none' && (
            <StateMessage
              state={this.validationstate}
              message={this.message}></StateMessage>
          )}
        </div>
      </Host>
    );
  }
}
